import React, { useCallback, useEffect, useMemo, useState } from "react";
import { auth } from "./firebase";
import { EmptyState, PageHeader, StatCard, ToneBadge } from "./petaarUi";

const PAYOUT_OPS_URL =
  process.env.REACT_APP_PAYOUT_OPS_URL ||
  "https://us-central1-petaar-3dcab.cloudfunctions.net/payoutOps";

const DEFAULT_SUMMARY = {
  gross: 0,
  commission: 0,
  netEarnings: 0,
  refundedAmount: 0,
  paidOutAmount: 0,
  pendingAmount: 0,
  approvedAmount: 0,
  availableToWithdraw: 0,
  paidBookingsCount: 0,
  payoutRequestsCount: 0,
};

function tsToMs(ts) {
  if (!ts) return 0;
  if (typeof ts === "number") return ts;
  if (typeof ts === "string") {
    const parsed = new Date(ts).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (ts.toMillis && typeof ts.toMillis === "function") return ts.toMillis();
  if (ts.seconds) return ts.seconds * 1000;
  if (ts._seconds) return ts._seconds * 1000;
  return 0;
}

function formatAmount(value) {
  return `Rs ${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatTs(ts) {
  const ms = tsToMs(ts);
  if (!ms) return "N/A";
  return new Date(ms).toLocaleString("en-IN");
}

function statusTone(status) {
  const value = String(status || "").toLowerCase();
  if (value === "paid") return "success";
  if (value === "approved") return "accent";
  if (value === "rejected") return "danger";
  if (value === "cancelled") return "default";
  return "warning";
}

export default function ProviderPayouts({ user }) {
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cancellingId, setCancellingId] = useState("");
  const [amount, setAmount] = useState("");
  const [payoutMode, setPayoutMode] = useState("upi");
  const [payoutAccount, setPayoutAccount] = useState("");
  const [note, setNote] = useState("");

  const callPayoutApi = useCallback(async (payload) => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("Session expired. Please login again.");
    }
    const res = await fetch(PAYOUT_OPS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.success) {
      throw new Error(data?.error || `Payout API failed (${res.status})`);
    }
    return data;
  }, []);

  const loadPayoutData = useCallback(async () => {
    if (!user?.uid) {
      setSummary(DEFAULT_SUMMARY);
      setRequests([]);
      return;
    }
    setLoading(true);
    try {
      setLoadError("");
      const data = await callPayoutApi({ action: "provider_summary", limit: 80 });
      setSummary({ ...DEFAULT_SUMMARY, ...(data.summary || {}) });
      setRequests(Array.isArray(data.requests) ? data.requests : []);
    } catch (error) {
      setLoadError(error.message || "Unable to load payout data.");
      setSummary(DEFAULT_SUMMARY);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [callPayoutApi, user?.uid]);

  useEffect(() => {
    loadPayoutData();
  }, [loadPayoutData]);

  const sortedRequests = useMemo(
    () => [...requests].sort((a, b) => tsToMs(b.createdAt) - tsToMs(a.createdAt)),
    [requests],
  );

  const submitRequest = async () => {
    try {
      const amountValue = Number(amount);
      if (!Number.isFinite(amountValue) || amountValue < 100) {
        alert("Minimum payout request is Rs 100.");
        return;
      }
      if (!payoutAccount.trim()) {
        alert("Please add payout account details.");
        return;
      }
      if (amountValue > Number(summary.availableToWithdraw || 0)) {
        alert("Requested amount is more than available withdrawal balance.");
        return;
      }
      setSubmitting(true);
      await callPayoutApi({
        action: "provider_request",
        amount: amountValue,
        payoutMode,
        payoutAccount: payoutAccount.trim(),
        note: note.trim(),
      });
      setAmount("");
      setNote("");
      alert("Payout request submitted.");
      await loadPayoutData();
    } catch (error) {
      alert(error.message || "Unable to submit payout request.");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelRequest = async (requestId) => {
    if (!requestId) return;
    try {
      setCancellingId(requestId);
      await callPayoutApi({
        action: "provider_cancel",
        requestId,
      });
      await loadPayoutData();
    } catch (error) {
      alert(error.message || "Unable to cancel payout request.");
    } finally {
      setCancellingId("");
    }
  };

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow="Provider Finance"
        title="Payouts"
        subtitle="Track withdrawable balance, send payout requests, and monitor every settlement status from one screen."
        actions={
          <button className="petaar-button" onClick={loadPayoutData} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        }
      />

      {loadError ? <div className="petaar-notice error" style={{ marginBottom: 16 }}>{loadError}</div> : null}

      <div className="petaar-stat-grid" style={{ marginBottom: 24 }}>
        <StatCard label="Net earnings" value={summary.netEarnings} prefix="Rs " decimals={2} tone="default" meta={`${summary.paidBookingsCount || 0} paid bookings`} />
        <StatCard label="Paid out" value={summary.paidOutAmount} prefix="Rs " decimals={2} tone="success" meta="Transferred to provider" />
        <StatCard label="Pending review" value={summary.pendingAmount} prefix="Rs " decimals={2} tone="warning" meta="Requested, awaiting admin" />
        <StatCard label="Available to withdraw" value={summary.availableToWithdraw} prefix="Rs " decimals={2} tone="accent" meta={`${summary.payoutRequestsCount || 0} request(s) total`} />
      </div>

      <div className="petaar-split" style={{ alignItems: "start", marginBottom: 24 }}>
        <section className="petaar-card" style={{ display: "grid", gap: 18 }}>
          <div>
            <div className="petaar-kicker">New Request</div>
            <h3 style={{ fontSize: "1.5rem", marginTop: 6 }}>Create withdrawal request</h3>
            <p style={{ margin: "8px 0 0", color: "var(--muted)", lineHeight: 1.6 }}>
              Minimum payout request is Rs 100. Funds already requested or approved are excluded from your available balance.
            </p>
          </div>

          <div className="petaar-form-grid">
            <input
              type="number"
              min={100}
              step="1"
              placeholder="Amount (INR)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <select value={payoutMode} onChange={(e) => setPayoutMode(e.target.value)}>
              <option value="upi">UPI</option>
              <option value="bank">Bank</option>
              <option value="manual">Manual transfer</option>
            </select>
            <input
              placeholder={payoutMode === "upi" ? "UPI ID" : payoutMode === "bank" ? "Bank details" : "Payout details"}
              value={payoutAccount}
              onChange={(e) => setPayoutAccount(e.target.value)}
            />
          </div>

          <textarea
            placeholder="Optional note for admin"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          {Number(summary.availableToWithdraw || 0) < 100 ? (
            <div className="petaar-notice info">
              Minimum request is Rs 100. Complete more paid bookings before creating a new withdrawal request.
            </div>
          ) : null}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              className="petaar-button"
              onClick={submitRequest}
              disabled={submitting || Number(summary.availableToWithdraw || 0) < 100}
            >
              {submitting ? "Submitting..." : "Request Payout"}
            </button>
          </div>
        </section>

        <section className="petaar-card" style={{ display: "grid", gap: 12 }}>
          <div className="petaar-kicker">Balance Breakdown</div>
          <div className="petaar-list-row" style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Gross collected</span><strong>{formatAmount(summary.gross)}</strong></div>
          </div>
          <div className="petaar-list-row" style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Commission</span><strong>{formatAmount(summary.commission)}</strong></div>
          </div>
          <div className="petaar-list-row" style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Refunded amount</span><strong>{formatAmount(summary.refundedAmount)}</strong></div>
          </div>
          <div className="petaar-list-row" style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Approved, unpaid</span><strong>{formatAmount(summary.approvedAmount)}</strong></div>
          </div>
        </section>
      </div>

      <section className="petaar-card">
        <div className="petaar-section-title">
          <div>
            <div className="petaar-kicker">History</div>
            <h3 style={{ fontSize: "1.35rem" }}>Payout request timeline</h3>
          </div>
          <ToneBadge tone="default">{sortedRequests.length} request(s)</ToneBadge>
        </div>

        {loading ? (
          <div className="petaar-grid" style={{ gap: 16 }}>
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={`payout-skeleton-${index}`} className="petaar-skeleton" style={{ height: 170, borderRadius: 18 }} />
            ))}
          </div>
        ) : sortedRequests.length === 0 ? (
          <EmptyState
            icon="💸"
            title="No payout requests yet"
            description="Your submitted requests will appear here with timestamps, status history, and admin notes."
          />
        ) : (
          <div className="petaar-grid" style={{ gap: 16 }}>
            {sortedRequests.map((item) => (
              <div key={item.id} className="petaar-list-row">
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                  <div>
                    <h4 style={{ fontSize: "1.1rem" }}>{formatAmount(item.amount)}</h4>
                    <div style={{ marginTop: 6, color: "var(--muted)" }}>
                      {String(item.payoutMode || "manual").toUpperCase()} · {formatTs(item.createdAt)}
                    </div>
                  </div>
                  <ToneBadge tone={statusTone(item.status)}>{item.status || "requested"}</ToneBadge>
                </div>

                <div style={{ marginTop: 12, display: "grid", gap: 8, color: "var(--muted)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Payout account</span><strong style={{ color: "var(--text)" }}>{item.payoutAccount || "N/A"}</strong></div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Admin note</span><strong style={{ color: "var(--text)" }}>{item.adminNote || "N/A"}</strong></div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span>Payout reference</span><strong style={{ color: "var(--text)" }}>{item.payoutReference || "N/A"}</strong></div>
                </div>

                {item.note ? (
                  <div className="petaar-notice info" style={{ marginTop: 12 }}>
                    Provider note: {item.note}
                  </div>
                ) : null}

                {item.status === "requested" ? (
                  <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
                    <button
                      className="petaar-button petaar-button--ghost"
                      onClick={() => cancelRequest(item.id)}
                      disabled={cancellingId === item.id}
                    >
                      {cancellingId === item.id ? "Cancelling..." : "Cancel Request"}
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
